/*
 * <reviews-carousel>
 *
 * Auto-advancing testimonial carousel with scroll-snap slides and progress dots.
 *
 * Implemented as a custom element on purpose: the cart drawer replaces
 * #CartDrawer-Body via innerHTML on every cart update (see cart.js), and inline
 * <script> tags do not re-execute after an innerHTML swap. Custom elements are
 * upgraded automatically whenever they are inserted, so the carousel keeps
 * working after add / remove / quantity changes.
 */
if (!customElements.get("reviews-carousel")) {
  class ReviewsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-fcc-track]");
      if (!this.track) return;

      this.slides = this.querySelectorAll("[data-fcc-slide]");
      this.dots = this.querySelectorAll("[data-fcc-dot]");
      this.count = this.slides.length;
      if (this.count < 2) return;

      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.index = 0;
      this.elapsed = 0;
      this.lastTs = 0;
      this.running = false;
      this.raf = null;
      this.hovered = false;
      this.touching = false;
      this.isProg = false;
      this.progTimer = null;
      this.settle = null;
      this.reduce =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.onEnter = () => {
        this.hovered = true;
        this.stop();
      };
      this.onLeave = () => {
        this.hovered = false;
        this.maybeStart();
      };
      this.onTouchStart = () => {
        this.touching = true;
        this.stop();
      };
      this.onTouchEnd = () => {
        this.touching = false;
        setTimeout(() => this.maybeStart(), 300);
      };
      this.onScroll = () => {
        if (this.isProg) return;
        if (this.settle) clearTimeout(this.settle);
        this.settle = setTimeout(() => {
          this.index = Math.round(this.track.scrollLeft / this.slideWidth());
          this.elapsed = 0;
          this.render(0);
          this.maybeStart();
        }, 400);
      };
      this.onResize = () => this.scrollToIndex(this.index, false);

      this.addEventListener("mouseenter", this.onEnter);
      this.addEventListener("mouseleave", this.onLeave);
      this.track.addEventListener("touchstart", this.onTouchStart, {
        passive: true
      });
      this.track.addEventListener("touchend", this.onTouchEnd);
      this.track.addEventListener("touchcancel", this.onTouchEnd);
      this.track.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize);

      this.dots.forEach((dot, i) => {
        dot.addEventListener("click", () => {
          this.index = i;
          this.elapsed = 0;
          this.render(0);
          this.scrollToIndex(i, true);
          this.maybeStart();
        });
      });

      this.render(0);
      this.start();
    }

    /*
     * Every listener and the rAF loop is torn down here. Without this the drawer
     * would leak a new animation loop on each cart update, since the old element
     * is discarded but its loop would keep running.
     */
    disconnectedCallback() {
      this.stop();
      if (this.progTimer) clearTimeout(this.progTimer);
      if (this.settle) clearTimeout(this.settle);
      this.removeEventListener("mouseenter", this.onEnter);
      this.removeEventListener("mouseleave", this.onLeave);
      if (this.track) {
        this.track.removeEventListener("touchstart", this.onTouchStart);
        this.track.removeEventListener("touchend", this.onTouchEnd);
        this.track.removeEventListener("touchcancel", this.onTouchEnd);
        this.track.removeEventListener("scroll", this.onScroll);
      }
      window.removeEventListener("resize", this.onResize);
    }

    slideWidth() {
      return (
        this.slides[0].getBoundingClientRect().width || this.track.clientWidth
      );
    }

    render(p) {
      this.dots.forEach((dot, i) => {
        const fill = dot.querySelector(".fcc-fill");
        if (!fill) return;
        let w = 0;
        if (i < this.index) {
          w = 100;
        } else if (i === this.index) {
          w = this.reduce ? 100 : Math.max(0, Math.min(1, p)) * 100;
        }
        fill.style.width = w + "%";
      });
    }

    scrollToIndex(i, smooth) {
      this.isProg = true;
      if (this.progTimer) clearTimeout(this.progTimer);
      const left = this.slideWidth() * i;
      try {
        this.track.scrollTo({ left: left, behavior: smooth ? "smooth" : "auto" });
      } catch (e) {
        this.track.scrollLeft = left;
      }
      this.progTimer = setTimeout(() => {
        this.isProg = false;
      }, 700);
    }

    tick(ts) {
      if (!this.running) return;
      const dt = ts - this.lastTs;
      this.lastTs = ts;
      this.elapsed += dt;
      let p = this.elapsed / this.interval;
      if (p >= 1) {
        this.index = (this.index + 1) % this.count;
        this.scrollToIndex(this.index, true);
        this.elapsed = 0;
        p = 0;
      }
      this.render(p);
      this.raf = requestAnimationFrame(this.tick.bind(this));
    }

    start() {
      if (this.reduce) {
        this.render(0);
        return;
      }
      if (this.running) return;
      this.running = true;
      this.lastTs = performance.now();
      this.raf = requestAnimationFrame(this.tick.bind(this));
    }

    stop() {
      this.running = false;
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }

    maybeStart() {
      if (!this.hovered && !this.touching) this.start();
    }
  }

  customElements.define("reviews-carousel", ReviewsCarousel);
}
